/**
 * Valid currency code that excludes the UNDEFINED sentinel value and unhandledEnumCase symbol.
 *
 * NOTE: We intentionally exclude the unhandledEnumCase symbol to avoid type complexity
 * at integration boundaries with GMST and external APIs. This means we lose compile-time
 * detection of new currencies during the window between backend adding them and frontend
 * regenerating types, but this is acceptable since:
 * 1. Types are regenerated regularly as part of development
 * 2. The impact is minimal (currency shows as undefined temporarily)
 * 3. We avoid 100+ boundary conversions throughout the codebase
 *
 * TODO: Once CHIRP regen (#19823) lands in this package, update the import source.
 */

/**
 * Valid country code that excludes the UNDEFINED sentinel value and unhandledEnumCase symbol.
 * See CurrencyCode documentation for rationale.
 */

/**
 * Type guard that checks if a value is a non-null string suitable for use as a CurrencyCode.
 *
 * Filters out undefined, null, non-strings, and the CHIRP 'UNDEFINED' sentinel value.
 * Use this for trusted backend responses where you can assume any valid string is a valid currency.
 *
 * For untrusted input (like query parameters), use isValidCurrencyCodeForQueryParam instead.
 */
export function isCurrencyCodeString(value) {
  return value !== undefined && value !== null && typeof value === 'string' && value !== 'UNDEFINED';
}

/**
 * Extracts the string value from a CurrencyCode, filtering out symbol types.
 * Returns undefined if the value is not a string.
 *
 * Use this when you need to pass a CurrencyCode to APIs or systems that only
 * accept string values (e.g., tracking, mutations, URL params).
 */
export function getCurrencyCodeString(code) {
  return typeof code === 'string' ? code : undefined;
}

/**
 * Type guard to check if a value is a valid CountryCode string.
 * Filters out undefined, null, non-strings, and the CHIRP 'UNDEFINED' sentinel value.
 */
export function isCountryCodeString(value) {
  return value !== undefined && value !== null && typeof value === 'string' && value !== 'UNDEFINED';
}

/**
 * Type guard to validate untrusted currency codes (like query params) against available currencies.
 * Use this for user input that needs validation against the actual list of available currencies.
 */
export function isValidCurrencyCodeForQueryParam(value, availableCurrencyCodes) {
  if (!isCurrencyCodeString(value)) {
    return false;
  }
  const upperValue = value.toUpperCase();
  return availableCurrencyCodes.some(code => typeof code === 'string' && code.toUpperCase() === upperValue);
}

/**
 * Local definition of CurrencyStore that uses CHIRP-generated types.
 */